import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import {
  loginWithFederatedSSO,
  persistSession
} from "../../services/authService";
import { HANDOFF_ALLOWED_ORIGINS } from "../../config/config";
import AuthBrandingPanel from "./AuthBrandingPanel";

/**
 * LexAI → Orbit SSO handoff receiver.
 *
 * A LexAI tenant portal opens this route, then posts the id_token it already
 * holds. Orbit exchanges it for its own session. The token arrives by
 * postMessage rather than in the URL on purpose: query strings leak through
 * Referer headers, browser history, and Cloud Run request logs.
 *
 * The server is what actually enforces trust — it checks the token against a
 * pinned (issuer, audience) pairing and admits it only into the one org that
 * pairing maps to (see FEDERATED_TRUST in the backend). The origin check here is
 * a first filter, not the security boundary.
 *
 * Any failure falls back to the normal login page. Handoff is a convenience; it
 * must never be the only way in.
 */

const MESSAGE_TYPE = "orbit-sso-handoff";
const READY_TYPE = "orbit-handoff-ready";
const TIMEOUT_MS = 15000;

const HandoffPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const handled = useRef(false);

  const fail = useCallback(
    (reason) => {
      if (handled.current) return;
      handled.current = true;
      // Log for support; the user just gets sent to the normal login page.
      console.warn("SSO handoff failed:", reason);
      setError(reason);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    },
    [navigate]
  );

  const exchange = useCallback(
    async ({ email, provider, credential }) => {
      if (handled.current) return;
      handled.current = true;

      const response = await loginWithFederatedSSO(email, provider, credential);

      if (!response.success || !response.access_token) {
        handled.current = false;
        fail(response.message || "We couldn't sign you in from LexAI.");
        return;
      }

      persistSession(response.access_token);
      navigate("/dashboard", { replace: true });
    },
    [navigate, fail]
  );

  useEffect(() => {
    if (HANDOFF_ALLOWED_ORIGINS.length === 0) {
      fail("Handoff is not enabled for this environment.");
      return;
    }

    const onMessage = (event) => {
      // Exact-origin match only. A suffix check here would let
      // evil-moe.lexai.nativeorange.ai.attacker.com through.
      if (!HANDOFF_ALLOWED_ORIGINS.includes(event.origin)) return;
      if (event.data?.type !== MESSAGE_TYPE) return;

      const { email, provider, credential } = event.data;
      if (!credential || !provider) {
        fail("The handoff message was incomplete.");
        return;
      }
      exchange({ email, provider, credential });
    };

    window.addEventListener("message", onMessage);

    // Tell the opener we're listening. It can't know when React has mounted, so
    // without this it would have to guess a delay before posting.
    if (window.opener) {
      HANDOFF_ALLOWED_ORIGINS.forEach((origin) =>
        window.opener.postMessage({ type: READY_TYPE }, origin)
      );
    }

    const timer = setTimeout(
      () => fail("LexAI didn't complete the handoff in time."),
      TIMEOUT_MS
    );

    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
    };
  }, [exchange, fail]);

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Blue Branding Section */}
      <AuthBrandingPanel />

      {/* Right Side - Handoff status */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md text-center">
          {error ? (
            <>
              <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-4" />
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                Couldn't sign you in
              </h1>
              <p className="text-sm text-gray-600">{error}</p>
              <p className="text-sm text-gray-500 mt-4">
                Taking you to the login page…
              </p>
            </>
          ) : (
            <>
              <Loader2 className="h-10 w-10 text-blue-600 mx-auto mb-4 animate-spin" />
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                Signing you in
              </h1>
              <p className="text-sm text-gray-600">
                Continuing with your LexAI account…
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HandoffPage;
